/**
 * AdminAvatarMenu — Sprint v1.1.
 *
 * 4-hub navigation model:
 *   • Administración → /admin (users, roles, templates, system, projects)
 *   • Finanzas       → /billing (credits, pricing, stripe, reports)
 *   • Soporte        → /admin?tab=support (AI ticket flow)
 *   • Cuenta         → /profile (perfil, seguridad, preferencias)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, LayoutDashboard, DollarSign, LifeBuoy, UserCircle,
  LogOut, Settings2, Coins, Layout, User, Languages, Volume2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../contexts/LocaleContext';

type HubKey = 'admin' | 'plantillas' | 'finanzas' | 'soporte' | 'settings';

// Sprint Estabilización P0.2 · Feb 2026 · las etiquetas ahora se
// resuelven vía useT() en el propio componente para evitar hard-code
// ES → resolvemos por key con `t('menu.<key>')`. Cada entrada expone
// solo su icon + route; el label se calcula en render.
const HUBS_ADMIN: Array<{
  key: HubKey; labelKey: string; icon: React.ComponentType<any>; route: string;
}> = [
  { key: 'admin',      labelKey: 'menu.admin',    icon: LayoutDashboard, route: '/admin' },
  { key: 'plantillas', labelKey: 'menu.templates', icon: Layout,          route: '/plantillas' },
  { key: 'finanzas',   labelKey: 'menu.credits',   icon: DollarSign,      route: '/creditos' },
  { key: 'soporte',    labelKey: 'menu.supportAi', icon: LifeBuoy,        route: '/soporte' },
  { key: 'settings',   labelKey: 'menu.myAccount', icon: Settings2,       route: '/perfil' },
];

const HUBS_USER: Array<{
  key: HubKey; labelKey: string; icon: React.ComponentType<any>; route: string;
}> = [
  { key: 'finanzas', labelKey: 'menu.creditsBilling', icon: Coins,     route: '/creditos' },
  { key: 'soporte',  labelKey: 'menu.contactSupport', icon: LifeBuoy,  route: '/soporte' },
  { key: 'settings', labelKey: 'menu.myAccount',      icon: Settings2, route: '/perfil' },
];

type Props = {
  isAdmin?: boolean;
  initials?: string;
  avatarUrl?: string | null;
  onLogout?: () => void;
};

export const AdminAvatarMenu: React.FC<Props> = ({ isAdmin: isAdminProp, initials: initialsProp, avatarUrl: avatarUrlProp, onLogout }) => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { logout, user } = useAuth();
  const t = useT();

  // Single source of truth: prefer explicit props, fall back to AuthContext.
  // This lets any consumer just drop <AdminAvatarMenu /> without wiring.
  const isAdmin = isAdminProp ?? (user?.role === 'admin');
  const initials = (initialsProp ?? (user?.name?.[0] || user?.email?.[0] || 'N')).toUpperCase();
  const rawAvatar = avatarUrlProp ?? user?.avatar_url ?? null;
  // Sprint Corrección Crítica · Cache-busting iOS Safari.
  // iOS Safari cachea agresivamente <img src>. `Perfil.uploadAvatar`
  // ya añade `?v=timestamp` al URL cuando el usuario sube uno nuevo,
  // por lo que solo aplicamos buster propio si aún no lo trae.
  const hasBuster = rawAvatar
    ? /[?&](v|t)=/i.test(rawAvatar)
    : false;
  const cacheKey = (user as any)?.updated_at || (user as any)?.avatar_updated_at || (user as any)?.id || 'nx';
  const withBuster = (u: string) =>
    hasBuster ? u : (u.includes('?') ? `${u}&t=${encodeURIComponent(cacheKey)}` : `${u}?t=${encodeURIComponent(cacheKey)}`);
  const avatarUrl = rawAvatar
    ? (rawAvatar.startsWith('data:'))
      ? rawAvatar // data URLs no necesitan cache busting
      : (rawAvatar.startsWith('http'))
        ? withBuster(rawAvatar)
        : withBuster((process.env.REACT_APP_BACKEND_URL || '') + rawAvatar)
    : null;

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', k);
    };
  }, [open]);

  const goRoute = (route: string) => { setOpen(false); nav(route); };
  // Sprint Final · Arquitectura Definitiva:
  // Al tocar el avatar en cualquier parte de la app, abrir directamente
  // la vista grande (/perfil). El popover pequeño se conserva únicamente
  // como fallback si el usuario mantiene pulsado (long-press) o hace
  // right-click, pero la acción por defecto es navegación directa.
  const openBigProfile = () => { setOpen(false); nav('/perfil'); };
  const hubs = isAdmin ? HUBS_ADMIN : HUBS_USER;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={openBigProfile}
        onContextMenu={(e) => { e.preventDefault(); setOpen(v => !v); }}
        data-testid="admin-avatar-btn"
        className="relative w-10 h-10 rounded-full overflow-hidden flex items-center justify-center font-semibold text-sm transition-all"
        style={{
          background: 'hsl(var(--accent) / 0.15)',
          border: '1px solid hsl(var(--accent) / 0.5)',
          color: 'hsl(var(--accent))',
        }}
        aria-label="Menú de usuario"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
        {isAdmin && (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full flex items-center justify-center"
            style={{ background: 'hsl(var(--accent))', color: '#020617' }}
          >
            <Shield size={9} strokeWidth={3} />
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="admin-avatar-menu"
          className="absolute right-0 top-12 z-50 w-80 rounded-3xl border overflow-hidden backdrop-blur-xl shadow-2xl"
          style={{
            background: 'hsl(var(--bg-elev) / 0.96)',
            borderColor: 'hsl(var(--accent) / 0.28)',
            boxShadow: '0 20px 60px -20px hsl(var(--accent) / 0.4)',
          }}
        >
          {/* Big profile header — matches Image 1 · pantalla 3 */}
          <header
            className="px-5 py-4 border-b flex items-center gap-3"
            style={{ borderColor: 'hsl(var(--border))' }}
          >
            <div
              className="w-12 h-12 rounded-full overflow-hidden grid place-items-center font-semibold text-base shrink-0"
              style={{
                background: 'hsl(var(--accent) / 0.18)',
                border: '1px solid hsl(var(--accent) / 0.5)',
                color: 'hsl(var(--accent))',
                boxShadow: '0 0 20px hsl(var(--accent) / 0.35)',
              }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                   style={{ color: 'hsl(var(--accent))' }}>
                {t('menu.myAccount')}
              </div>
              <div className="text-sm font-medium truncate mt-0.5" style={{ color: 'hsl(var(--text))' }}>
                {user?.name || (isAdmin ? 'Admin Nexus' : t('menu.userFallback'))}
              </div>
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] uppercase tracking-widest font-medium"
                   style={{
                     background: 'hsl(var(--accent) / 0.12)',
                     border: '1px solid hsl(var(--accent) / 0.3)',
                     color: 'hsl(var(--accent))',
                   }}>
                <Shield size={9} strokeWidth={2.5} />
                {isAdmin ? t('menu.planPro') : t('menu.planFree')}
              </div>
            </div>
          </header>

          <div className="py-2">
            {/* v1.17 · point #2 — Unified USER menu (idéntico al de /perfil).
                Administradores ven al final un enlace extra a /admin. */}
            <MenuItem icon={User} label={t('menu.myAccount')} onClick={() => goRoute('/perfil/cuenta')} testId="menu-mi-cuenta" />
            <MenuItem icon={Coins} label={t('menu.credits')} onClick={() => goRoute('/creditos')} testId="menu-creditos" />
            <MenuItem icon={Languages} label={t('menu.language')} onClick={() => goRoute('/perfil/idioma')} testId="menu-idioma" />
            <MenuItem icon={Volume2} label={t('menu.voiceAudio')} onClick={() => goRoute('/perfil/audio')} testId="menu-audio" />
            <MenuItem icon={LifeBuoy} label={t('menu.support')} onClick={() => goRoute('/soporte')} testId="menu-soporte" />
            {isAdmin && (
              <>
                <div className="border-t my-1" style={{ borderColor: 'hsl(var(--border))' }} />
                <MenuItem icon={LayoutDashboard} label={t('menu.adminPanel')} onClick={() => goRoute('/admin')} testId="menu-admin-hub" />
              </>
            )}
            <div className="border-t my-1" style={{ borderColor: 'hsl(var(--border))' }} />
            <MenuItem
              icon={LogOut}
              label={t('profile.signOut')}
              onClick={() => { setOpen(false); onLogout ? onLogout() : logout(); }}
              testId="menu-logout"
              variant="danger"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{
  icon: React.ComponentType<any>;
  label: string;
  onClick: () => void;
  testId: string;
  variant?: 'default' | 'danger';
}> = ({ icon: Icon, label, onClick, testId, variant = 'default' }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition text-left"
    style={{
      color: variant === 'danger' ? 'hsl(0 84% 62%)' : 'hsl(var(--text))',
    }}
  >
    <Icon
      size={16}
      style={{
        color: variant === 'danger' ? 'hsl(0 84% 62%)' : 'hsl(var(--accent))',
      }}
    />
    <span className="flex-1">{label}</span>
  </button>
);
